import {
  createBrowserLocalAuthorProvider,
} from "./local-author-provider.js";
import {
  canonicalAuthorshipProfileDisplayName,
  canonicalAuthorshipProfileHandle,
  historicalAuthorshipProfileProjection,
} from "./authorship-profile-values.js";

const DOCUMENT_VERSION = 1;
const DOCUMENT_FIELDS = Object.freeze([
  "authorId",
  "displayName",
  "documentVersion",
  "handle",
  "revision",
]);
const MAX_STORED_LENGTH = 4_096;
const MAX_AUTHOR_ID_LENGTH = 128;
const MAX_REVISION_LENGTH = 128;
const BROWSER_REPOSITORY_PROPERTY = "__fishystuffLocalAuthorRepository";
const REPOSITORY_BRAND = Symbol.for("fishystuff.local-author-repository.v1");
const BROWSER_REPOSITORY_BRAND = Symbol.for(
  "fishystuff.browser-local-author-repository.v1",
);
const BROWSER_REPOSITORY_LIFECYCLE = Symbol.for(
  "fishystuff.browser-local-author-repository-lifecycle.v1",
);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function invalid(reason) {
  return { kind: "invalid", reason };
}

function unavailable(operation, reason) {
  return { kind: "unavailable", operation, reason };
}

function canonicalAuthorId(value) {
  if (typeof value !== "string") {
    throw new TypeError("Local author ID must be text.");
  }
  const authorId = value.trim();
  if (!authorId || authorId.length > MAX_AUTHOR_ID_LENGTH) {
    throw new TypeError("Local author ID must be a bounded non-empty string.");
  }
  return authorId;
}

function exactCommand(value, fields, label) {
  if (!isRecord(value)) {
    throw new TypeError(`${label} must be an object.`);
  }
  const actualFields = Object.keys(value).sort();
  if (
    actualFields.length !== fields.length
    || actualFields.some((field, index) => field !== fields[index])
  ) {
    throw new TypeError(`${label} contains unsupported or missing fields.`);
  }
}

function canonicalSavedIdentity(value) {
  exactCommand(value, ["displayName", "handle"], "Local author identity");
  return {
    displayName: canonicalAuthorshipProfileDisplayName(value.displayName),
    handle: canonicalAuthorshipProfileHandle(value.handle),
  };
}

function canonicalImportedIdentity(value) {
  exactCommand(
    value,
    ["authorId", "displayName", "handle"],
    "Legacy local author identity",
  );
  const identity = {
    authorId: canonicalAuthorId(value.authorId),
    displayName: canonicalAuthorshipProfileDisplayName(value.displayName, {
      allowEmpty: true,
    }),
    handle: canonicalAuthorshipProfileHandle(value.handle, {
      allowEmpty: true,
    }),
  };
  if (
    identity.authorId !== value.authorId
    || identity.displayName !== value.displayName
    || identity.handle !== value.handle
  ) {
    throw new TypeError("Legacy local author identity must already be canonical.");
  }
  return identityWithDeterministicDefaults(identity);
}

function decodeStoredDocument(raw) {
  if (typeof raw !== "string") {
    return invalid("stored-value-must-be-a-string");
  }
  if (raw.length > MAX_STORED_LENGTH) {
    return invalid("stored-value-exceeds-v1-limit");
  }

  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return invalid("malformed-json");
  }
  if (!isRecord(document)) {
    return invalid("document-must-be-an-object");
  }
  if (
    Number.isSafeInteger(document.documentVersion)
    && document.documentVersion > DOCUMENT_VERSION
  ) {
    return {
      kind: "unsupportedVersion",
      foundVersion: document.documentVersion,
    };
  }

  const fields = Object.keys(document).sort();
  if (
    fields.length !== DOCUMENT_FIELDS.length
    || fields.some((field, index) => field !== DOCUMENT_FIELDS[index])
  ) {
    return invalid("document-fields-must-match-v1");
  }
  if (document.documentVersion !== DOCUMENT_VERSION) {
    return invalid("document-version-must-be-1");
  }
  if (
    typeof document.revision !== "string"
    || document.revision.length === 0
    || document.revision.length > MAX_REVISION_LENGTH
    || document.revision.trim() !== document.revision
  ) {
    return invalid("revision-must-be-a-bounded-non-empty-string");
  }

  let authorId;
  let displayName;
  let handle;
  try {
    authorId = canonicalAuthorId(document.authorId);
    displayName = canonicalAuthorshipProfileDisplayName(document.displayName, {
      allowEmpty: true,
    });
    handle = canonicalAuthorshipProfileHandle(document.handle, {
      allowEmpty: true,
    });
  } catch (_error) {
    return invalid("identity-fields-must-be-valid");
  }
  if (
    authorId !== document.authorId
    || displayName !== document.displayName
    || handle !== document.handle
  ) {
    return invalid("identity-fields-must-be-canonical");
  }
  if (!displayName || !handle) {
    return invalid("identity-fields-must-be-non-empty");
  }

  return {
    kind: "ready",
    revision: document.revision,
    authorId,
    displayName,
    handle,
  };
}

function expectedRevisionFrom(options) {
  if (options == null) {
    return undefined;
  }
  if (!isRecord(options)) {
    throw new TypeError("Repository command options must be an object.");
  }
  const fields = Object.keys(options);
  if (
    fields.length > 1
    || fields.some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError("Repository command options contain unsupported fields.");
  }
  if (options.expectedRevision === undefined) {
    return undefined;
  }
  if (options.expectedRevision === null) {
    return null;
  }
  if (
    typeof options.expectedRevision !== "string"
    || options.expectedRevision.length === 0
    || options.expectedRevision.length > MAX_REVISION_LENGTH
    || options.expectedRevision.trim() !== options.expectedRevision
  ) {
    throw new TypeError(
      "Expected revision must be null or a bounded non-empty string.",
    );
  }
  return options.expectedRevision;
}

function freezeState(value) {
  return Object.freeze({ ...value });
}

function cloneState(value) {
  return freezeState(value);
}

function statesEqual(left, right) {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key, index) => (
      key === rightKeys[index]
      && left[key] === right[key]
    ));
}

function readyIdentityEqual(left, right) {
  return left.authorId === right.authorId
    && left.displayName === right.displayName
    && left.handle === right.handle;
}

function identityFieldsEqual(state, identity) {
  return state.displayName === identity.displayName
    && state.handle === identity.handle;
}

function conflictState(expectedRevision, authoritative) {
  if (authoritative.kind === "ready") {
    return {
      kind: "conflict",
      expectedRevision,
      actualRevision: authoritative.revision,
      authorId: authoritative.authorId,
      displayName: authoritative.displayName,
      handle: authoritative.handle,
    };
  }
  return {
    kind: "conflict",
    expectedRevision,
    actualRevision: null,
    authorId: null,
    displayName: "",
    handle: "",
  };
}

function defaultCreateRevision() {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function defaultCreateAuthorId() {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return `author_${cryptoRef.randomUUID()}`;
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return `author_${Array.from(bytes, (value) => (
      value.toString(16).padStart(2, "0")
    )).join("")}`;
  }
  return `author_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function fallbackToken(authorId) {
  const segments = authorId.split(/[-_]+/).filter(Boolean);
  const candidate = (segments.at(-1) || authorId).replace(/[^a-z0-9]/gi, "");
  if (candidate) {
    return candidate.slice(0, 8);
  }

  let hash = 2_166_136_261;
  for (const character of authorId) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function identityWithDeterministicDefaults(identity) {
  const token = fallbackToken(identity.authorId);
  return {
    authorId: identity.authorId,
    displayName: identity.displayName || `Local author ${token.toUpperCase()}`,
    handle: identity.handle || `local-${token.toLowerCase()}`,
  };
}

export function createLocalAuthorRepository({
  provider = null,
  createRevision = defaultCreateRevision,
  createAuthorId = defaultCreateAuthorId,
} = {}) {
  if (
    !provider
    || typeof provider.dispose !== "function"
    || typeof provider.readDocument !== "function"
    || typeof provider.removeDocument !== "function"
    || typeof provider.subscribeInvalidation !== "function"
    || typeof provider.writeDocument !== "function"
  ) {
    throw new TypeError(
      "Local author repository requires its exact local provider.",
    );
  }
  if (typeof createRevision !== "function") {
    throw new TypeError("createRevision must be a function.");
  }
  if (typeof createAuthorId !== "function") {
    throw new TypeError("createAuthorId must be a function.");
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let disposed = false;
  const listeners = new Set();

  function state() {
    return cloneState(currentState);
  }

  function historicalAuthor() {
    return historicalAuthorshipProfileProjection(currentState);
  }

  function deliver(listener) {
    try {
      listener(state());
    } catch (error) {
      globalThis.reportError?.(error);
    }
  }

  function publish(nextState) {
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) {
      return state();
    }
    currentState = frozen;
    if (!disposed) {
      for (const listener of listeners) {
        deliver(listener);
      }
    }
    return state();
  }

  function readAuthoritativeState() {
    const stored = provider.readDocument();
    if (stored.kind === "unavailable") {
      return unavailable(stored.operation, stored.reason);
    }
    if (stored.raw === null) {
      return { kind: "missing" };
    }
    return decodeStoredDocument(stored.raw);
  }

  function load() {
    return publish(readAuthoritativeState());
  }

  function nextRevision() {
    try {
      const revision = createRevision();
      if (
        typeof revision !== "string"
        || revision.length === 0
        || revision.length > MAX_REVISION_LENGTH
        || revision.trim() !== revision
      ) {
        throw new TypeError("Revision must be a bounded non-empty string.");
      }
      return { ok: true, revision };
    } catch (_error) {
      return { ok: false };
    }
  }

  function nextAuthorId() {
    try {
      const generated = createAuthorId();
      const authorId = canonicalAuthorId(generated);
      if (authorId !== generated) {
        throw new TypeError("Generated local author ID must already be canonical.");
      }
      return { ok: true, authorId };
    } catch (_error) {
      return { ok: false };
    }
  }

  function writeReady(identity, previousRevision = null) {
    const revisionResult = nextRevision();
    if (!revisionResult.ok) {
      return publish(unavailable("write", "revision-generation-failed"));
    }
    if (
      previousRevision !== null
      && revisionResult.revision === previousRevision
    ) {
      return publish(unavailable("write", "revision-generation-collision"));
    }

    const document = {
      documentVersion: DOCUMENT_VERSION,
      revision: revisionResult.revision,
      authorId: identity.authorId,
      displayName: identity.displayName,
      handle: identity.handle,
    };
    const written = provider.writeDocument(JSON.stringify(document));
    if (written.kind === "unavailable") {
      return publish(unavailable(written.operation, written.reason));
    }
    return publish({
      kind: "ready",
      revision: document.revision,
      authorId: document.authorId,
      displayName: document.displayName,
      handle: document.handle,
    });
  }

  function writeGeneratedIdentity(identity) {
    const authorIdResult = nextAuthorId();
    if (!authorIdResult.ok) {
      return publish(unavailable("write", "author-id-generation-failed"));
    }
    return writeReady(identityWithDeterministicDefaults({
      authorId: authorIdResult.authorId,
      displayName: identity.displayName,
      handle: identity.handle,
    }));
  }

  function mutationAuthority() {
    const authoritative = readAuthoritativeState();
    if (
      authoritative.kind === "invalid"
      || authoritative.kind === "unsupportedVersion"
      || authoritative.kind === "unavailable"
    ) {
      publish(authoritative);
      return { blocked: true, state: state() };
    }
    return { blocked: false, state: authoritative };
  }

  async function saveIdentity(identity, options = {}) {
    const command = canonicalSavedIdentity(identity);
    const expectedRevision = expectedRevisionFrom(options);
    const authority = mutationAuthority();
    if (authority.blocked) {
      return authority.state;
    }
    const authoritative = authority.state;

    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return publish(conflictState(expectedRevision, authoritative));
      }
      return writeGeneratedIdentity(command);
    }

    if (
      expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      if (identityFieldsEqual(authoritative, command)) {
        return publish(authoritative);
      }
      return publish(conflictState(expectedRevision, authoritative));
    }
    if (identityFieldsEqual(authoritative, command)) {
      return publish(authoritative);
    }
    return writeReady({
      authorId: authoritative.authorId,
      displayName: command.displayName,
      handle: command.handle,
    }, authoritative.revision);
  }

  async function ensureAuthor(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authority = mutationAuthority();
    if (authority.blocked) {
      return authority.state;
    }
    const authoritative = authority.state;

    if (authoritative.kind === "ready") {
      return publish(authoritative);
    }
    if (expectedRevision !== undefined && expectedRevision !== null) {
      return publish(conflictState(expectedRevision, authoritative));
    }
    return writeGeneratedIdentity({ displayName: "", handle: "" });
  }

  async function importLegacyIdentity(identity, options = {}) {
    const command = canonicalImportedIdentity(identity);
    const expectedRevision = expectedRevisionFrom(options);
    const authority = mutationAuthority();
    if (authority.blocked) {
      return authority.state;
    }
    const authoritative = authority.state;

    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return publish(conflictState(expectedRevision, authoritative));
      }
      return writeReady(command);
    }
    if (readyIdentityEqual(authoritative, command)) {
      return publish(authoritative);
    }
    return publish(conflictState(
      expectedRevision === undefined ? null : expectedRevision,
      authoritative,
    ));
  }

  async function reset(options = {}) {
    const expectedRevision = expectedRevisionFrom(options);
    const authoritative = readAuthoritativeState();

    if (authoritative.kind === "unavailable") {
      return publish(authoritative);
    }
    if (authoritative.kind === "missing") {
      if (expectedRevision !== undefined && expectedRevision !== null) {
        return publish(conflictState(expectedRevision, authoritative));
      }
      return publish(authoritative);
    }
    if (
      authoritative.kind === "ready"
      && expectedRevision !== undefined
      && expectedRevision !== authoritative.revision
    ) {
      return publish(conflictState(expectedRevision, authoritative));
    }

    const removed = provider.removeDocument();
    if (removed.kind === "unavailable") {
      return publish(unavailable(removed.operation, removed.reason));
    }
    return publish({ kind: "missing" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Repository listener must be a function.");
    }
    if (disposed) {
      return () => {};
    }
    listeners.add(listener);
    deliver(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  const unsubscribeInvalidation = provider.subscribeInvalidation(() => {
    if (!disposed) load();
  });

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    listeners.clear();
    unsubscribeInvalidation();
    provider.dispose();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: DOCUMENT_VERSION,
    load,
    state,
    historicalAuthor,
    saveIdentity,
    ensureAuthor,
    importLegacyIdentity,
    reset,
    subscribe,
    dispose,
  });
}

let browserRepository = null;
let browserRepositoryWindow = null;

function isCompatibleBrowserRepository(value) {
  return Boolean(
    value
    && value[BROWSER_REPOSITORY_BRAND] === DOCUMENT_VERSION
    && typeof value.load === "function"
    && typeof value.state === "function"
    && typeof value.historicalAuthor === "function"
    && typeof value.saveIdentity === "function"
    && typeof value.ensureAuthor === "function"
    && typeof value.importLegacyIdentity === "function"
    && typeof value.reset === "function"
    && typeof value.subscribe === "function"
    && typeof value[BROWSER_REPOSITORY_LIFECYCLE]?.isActive === "function"
    && typeof value[BROWSER_REPOSITORY_LIFECYCLE]?.release === "function",
  );
}

function browserRepositoryView(repository, windowRef) {
  let active = true;
  let view = null;
  const lifecycle = Object.freeze({
    isActive: () => active,
    release() {
      if (!active) return;
      active = false;
      repository.dispose();
      if (windowRef?.[BROWSER_REPOSITORY_PROPERTY] === view) {
        delete windowRef[BROWSER_REPOSITORY_PROPERTY];
      }
    },
  });
  view = Object.freeze({
    [BROWSER_REPOSITORY_BRAND]: DOCUMENT_VERSION,
    [BROWSER_REPOSITORY_LIFECYCLE]: lifecycle,
    load: repository.load,
    state: repository.state,
    historicalAuthor: repository.historicalAuthor,
    saveIdentity: repository.saveIdentity,
    ensureAuthor: repository.ensureAuthor,
    importLegacyIdentity: repository.importLegacyIdentity,
    reset: repository.reset,
    subscribe: repository.subscribe,
  });
  return view;
}

export function createBrowserLocalAuthorRepository() {
  return createLocalAuthorRepository({
    provider: createBrowserLocalAuthorProvider(),
  });
}

export function releaseBrowserLocalAuthorRepository(repository) {
  if (!isCompatibleBrowserRepository(repository)) {
    throw new TypeError(
      "The released local author repository has an incompatible contract.",
    );
  }
  repository[BROWSER_REPOSITORY_LIFECYCLE].release();
  if (browserRepository === repository) {
    browserRepository = null;
    browserRepositoryWindow = null;
  }
}

export function browserLocalAuthorRepository() {
  const windowRef = globalThis.window;
  if (windowRef && typeof windowRef === "object") {
    const sharedRepository = windowRef[BROWSER_REPOSITORY_PROPERTY];
    if (sharedRepository !== undefined) {
      if (!isCompatibleBrowserRepository(sharedRepository)) {
        throw new TypeError(
          "The shared local author repository has an incompatible contract.",
        );
      }
      browserRepository = sharedRepository;
      browserRepositoryWindow = windowRef;
    }
  }

  if (
    browserRepository
    && !browserRepository[BROWSER_REPOSITORY_LIFECYCLE].isActive()
  ) {
    browserRepository = null;
    browserRepositoryWindow = null;
  }

  // A module instance can outlive a synthetic browsing context in tests and
  // embedded hosts. The window slot, not the module global, identifies the
  // in-page singleton. Never carry a provider bound to one context into a
  // different context that has no adopted repository.
  if (browserRepository && browserRepositoryWindow !== (windowRef ?? null)) {
    browserRepository = null;
    browserRepositoryWindow = null;
  }

  if (!browserRepository) {
    const repository = createBrowserLocalAuthorRepository();
    repository.load();
    browserRepository = browserRepositoryView(repository, windowRef ?? null);
    browserRepositoryWindow = windowRef ?? null;
  }

  if (windowRef && typeof windowRef === "object") {
    windowRef[BROWSER_REPOSITORY_PROPERTY] = browserRepository;
  }
  return browserRepository;
}

export function reloadActiveBrowserLocalAuthorRepository() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") return null;
  const sharedRepository = windowRef[BROWSER_REPOSITORY_PROPERTY];
  if (sharedRepository === undefined) return null;
  if (!isCompatibleBrowserRepository(sharedRepository)) {
    throw new TypeError(
      "The active local author repository has an incompatible contract.",
    );
  }
  return sharedRepository.load();
}
